"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { SubscriptionTier } from "@/lib/billing/plans";

interface CancellationConfirmationModalProps {
  open: boolean;
  tier: SubscriptionTier;
  periodEndDate: string | null;
  activeOrgCount: number;
  serverLimit: number;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

const TIER_DISPLAY: Record<string, string> = {
  starter: "Founder Starter",
  plus: "Founder Plus",
  pro: "Founder Pro",
  enterprise: "Enterprise",
};

function CheckIcon() {
  return (
    <svg className="mt-0.5 h-4 w-4 shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

export function CancellationConfirmationModal({
  open,
  tier,
  periodEndDate,
  activeOrgCount,
  serverLimit,
  onConfirm,
  onCancel,
}: CancellationConfirmationModalProps) {
  const [confirming, setConfirming] = useState(false);
  const tierName = TIER_DISPLAY[tier] ?? tier;

  if (!open) return null;

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative z-50 w-full max-w-lg rounded-lg border border-border bg-background shadow-lg">
        {/* Header */}
        <div className="border-b border-border px-6 py-5">
          <h3 className="text-lg font-semibold">Cancel your {tierName} subscription?</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            We&apos;d hate to see you go. Here&apos;s what happens if you cancel:
          </p>
        </div>

        {/* What you'll lose */}
        <div className="space-y-4 px-6 py-5">
          <div className="rounded-lg border border-red-200 bg-red-50/50 p-4 dark:border-red-900 dark:bg-red-950/20">
            <p className="mb-3 text-sm font-medium text-red-800 dark:text-red-300">
              After your grace period ends, you&apos;ll lose:
            </p>
            <ul className="space-y-2">
              {activeOrgCount > 0 && (
                <li className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
                  <CheckIcon />
                  <span>
                    {activeOrgCount} active organization{activeOrgCount !== 1 ? "s" : ""} will be archived
                  </span>
                </li>
              )}
              {serverLimit > 0 && (
                <li className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
                  <CheckIcon />
                  <span>Up to {serverLimit} game server listing{serverLimit !== 1 ? "s" : ""}</span>
                </li>
              )}
              <li className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
                <CheckIcon />
                <span>All {tierName} features and priority access</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
                <CheckIcon />
                <span>Game server plugin access for your players</span>
              </li>
            </ul>
          </div>

          {/* Grace period info */}
          <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50/50 p-4 dark:border-green-900 dark:bg-green-950/20">
            <ShieldIcon />
            <div>
              <p className="text-sm font-medium text-green-800 dark:text-green-300">
                7-day grace period
              </p>
              <p className="mt-0.5 text-sm text-green-700 dark:text-green-400">
                You&apos;ll keep full access for 7 days after cancellation. During that time you can
                resubscribe instantly to restore everything — no data is lost.
              </p>
            </div>
          </div>

          {periodEndDate && (
            <p className="text-sm text-muted-foreground">
              Your billing period ends on <span className="font-medium text-foreground">{periodEndDate}</span>.
              You won&apos;t be charged again.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse gap-3 border-t border-border px-6 py-4 sm:flex-row sm:justify-end">
          <Button
            variant="default"
            onClick={onCancel}
            disabled={confirming}
          >
            Keep my subscription
          </Button>
          <Button
            variant="outline"
            className="border-red-200 text-red-600 hover:border-red-400 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:border-red-600 dark:hover:bg-red-900/20"
            onClick={handleConfirm}
            disabled={confirming}
          >
            {confirming ? "Cancelling..." : "Yes, cancel subscription"}
          </Button>
        </div>
      </div>
    </div>
  );
}
